import moment from 'moment';
import api from '.';
import { ContentTypeLabel } from '../common/enum/ContentTypeLabel';
import downloadFile from '../functions/downloadFile';
import { Purpose } from '../pages/MasterData/Purpose/purposeSlice';
import { Revision } from '../pages/MasterData/Revision/revisionSlice';

interface GetProjectDocumentRevisions {
  projectDocumentId?: number | null;
  page: number;
  limit: number;
  sortBy?: string | null;
  orderBy?: 'desc' | 'asc' | null;
}

export interface ProjectDocumentRevision {
  purpose: Purpose | null;
  revision: Revision | null;
  manHour: number | null;
  planIssueDate: Date;
  planCompletedDate: Date;
  isAutoApproval: boolean;
  projectDocumentId: number | null;
}

interface UpdateProjectDocumentRevision extends ProjectDocumentRevision {
  id: number | null;
}

export interface RevisionNoteAndLink {
  id: number;
  note?: string | null;
  linkName?: string | null;
  linkUrl?: string | null;
}

interface QueryGetProjectDocumentRevisionCoversheets {
  projectDocumentRevisionId: number;
  filename: string;
  isDownload?: boolean;
  isPreview?: boolean;
}

export async function getProjectDocumentRevisions({
  projectDocumentId,
  page,
  limit,
  sortBy,
  orderBy
}: GetProjectDocumentRevisions) {
  const params = {
    projectDocumentId,
    page,
    limit,
    sortBy,
    orderBy
  };
  const { data } = await api.get('/project-document-revisions', { params });
  return data;
}

export async function getProjectDocumentRevision(id: number) {
  const { data } = await api.get(`/project-document-revisions/${id}`);
  return data;
}

export async function getProjectDocumentRevisionApproval(id: number) {
  const { data } = await api.get(`/project-document-revisions/${id}/approvals`);
  return data;
}

export async function createProjectDocumentRevision(
  documentRevisions: ProjectDocumentRevision[]
) {
  const projectDocumentRevision = documentRevisions.map((documentRevision) => {
    return {
      purposeId: documentRevision.purpose ? documentRevision.purpose.id : null,
      revisionId: documentRevision.revision
        ? documentRevision.revision.id
        : null,
      manHour: documentRevision.manHour,
      planIssueDate: moment(documentRevision.planIssueDate)
        .endOf('day')
        .toISOString(),
      planCompletedDate: moment(documentRevision.planCompletedDate)
        .endOf('day')
        .toISOString(),
      isAutoApproval: documentRevision.isAutoApproval,
      projectDocumentId: documentRevision.projectDocumentId
    };
  });
  const { data } = await api.post(
    '/project-document-revisions',
    projectDocumentRevision
  );
  return data;
}

export async function updateProjectDocumentRevision(
  revisionDocument: UpdateProjectDocumentRevision
) {
  const projectDocumentRevision = {
    id: revisionDocument.id,
    purposeId: revisionDocument.purpose ? revisionDocument.purpose.id : null,
    revisionId: revisionDocument.revision ? revisionDocument.revision.id : null,
    manHour: revisionDocument.manHour,
    planIssueDate: moment(revisionDocument.planIssueDate)
      .endOf('day')
      .toISOString(),
    planCompletedDate: moment(revisionDocument.planCompletedDate)
      .endOf('day')
      .toISOString(),
    isAutoApproval: revisionDocument.isAutoApproval,
    projectDocumentId: revisionDocument.projectDocumentId
  };
  const { data } = await api.put(
    `/project-document-revisions/${projectDocumentRevision.id}`,
    projectDocumentRevision
  );
  return data;
}

export async function updateRevisionNoteAndLink(
  revisionNoteAndLink: RevisionNoteAndLink
) {
  const { data } = await api.put(
    `/project-document-revisions/${revisionNoteAndLink.id}/notes`,
    revisionNoteAndLink
  );
  return data;
}

export function deleteProjectDocumentRevision(id: number) {
  return api.delete(`/project-document-revisions/${id}`);
}

export async function getProjectDocumentRevisionCoversheets({
  projectDocumentRevisionId,
  filename,
  isDownload = true,
  isPreview = false
}: QueryGetProjectDocumentRevisionCoversheets): Promise<void> {
  const params = {
    timeZone: moment().utcOffset()
  };
  const { data } = await api.get(
    `/project-document-revisions/${projectDocumentRevisionId}/generate-coversheets`,
    { responseType: 'blob', params }
  );
  downloadFile({
    data,
    contentType: ContentTypeLabel.pdf,
    filename,
    isDownload,
    isPreview
  });
}
